import React, { useEffect, useState } from 'react'
import { useAuth } from '../hooks/useAuth'

const GoogleLogin: React.FC = () => {
  const { user, isAuthenticated, isLoading, error, signInWithGoogle, signOut, initializeAuth } = useAuth()

  useEffect(() => {
    // Debug: Log environment variables
    console.log('🔧 Debug: Environment Variables')
    console.log('VITE_GOOGLE_CLIENT_ID:', import.meta.env.VITE_GOOGLE_CLIENT_ID)
    console.log('VITE_GOOGLE_CLIENT_SECRET:', import.meta.env.VITE_GOOGLE_CLIENT_SECRET ? '***SET***' : 'NOT SET')
    console.log('VITE_GOOGLE_REDIRECT_URI:', import.meta.env.VITE_GOOGLE_REDIRECT_URI)
    
    initializeAuth()
  }, [initializeAuth])

  const handleGoogleSignIn = async () => {
    try {
      console.log('🔧 Debug: Attempting Google Sign-In...')
      await signInWithGoogle()
    } catch (error) {
      console.error('🔧 Debug: Google sign in failed:', error)
      // Show error in UI instead of alert
      console.log('Please update Google Cloud Console with correct origins and redirect URIs')
    }
  }

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (error) {
      console.error('Sign out failed:', error)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="card-glass p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-300">Initializing authentication...</p>
        </div>
      </div>
    )
  }

  if (isAuthenticated && user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="card-glass p-8 text-center max-w-md w-full">
          <div className="mb-6">
            {user.picture && (
              <img
                src={user.picture}
                alt={user.name}
                className="w-16 h-16 rounded-full mx-auto mb-4 border-2 border-blue-500"
              />
            )}
            <h2 className="gradient-text text-2xl font-bold mb-2 font-mono">
              Welcome, {user.name}!
            </h2>
            <p className="text-gray-300 text-sm mb-6 font-sans">
              {user.email}
            </p>
          </div>

          <div className="space-y-4">
            <button
              onClick={handleSignOut}
              className="btn-secondary w-full"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center min-h-screen relative">
      {/* Floating decorative elements */}
      <div className="absolute top-20 left-20 w-4 h-4 bg-blue-500 rounded-full opacity-30 floating-element"></div>
      <div className="absolute top-40 right-32 w-6 h-6 bg-purple-500 rounded-full opacity-20 floating-element"></div>
      <div className="absolute bottom-32 left-32 w-3 h-3 bg-cyan-500 rounded-full opacity-25 floating-element"></div>
      <div className="absolute bottom-20 right-20 w-5 h-5 bg-green-500 rounded-full opacity-15 floating-element"></div>

      <div className="card-glass max-w-md w-full space-y-8 p-8 relative z-10">
        <div className="text-center">
          <div className="mb-6">
            {/* Logo/Icon */}
            <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>

            <h2 className="gradient-text text-3xl font-bold mb-2 font-mono">
              Architech Client Dashboard
            </h2>
            <p className="text-gray-300 font-sans">
              Internal Client Management System
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <button
              onClick={handleGoogleSignIn}
              className="btn-primary w-full flex items-center justify-center space-x-3"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>Sign in with Google</span>
            </button>

            <div className="text-center">
              <p className="text-gray-400 text-sm mb-4">
                Sign in with your @architech.org or @architechlab.org account
              </p>
              
              {error && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-red-400 text-sm">
                    {error.includes('popup_closed') ? 'Sign-in was cancelled' : 
                     error.includes('access_denied') ? 'Access denied. Please use an authorized email domain.' :
                     'Google OAuth error. Please check your browser console for details.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GoogleLogin 